import { when } from 'mobx'
import { observer } from 'mobx-react-lite'
import { WebMCPProvider, useMcpTool } from 'webmcp-react'
import { useWebMcpEnabled } from 'entities/WebMcp/lib/flag'
import { okResult, errorResult } from 'entities/WebMcp/lib/toolResult'
import { searchContactsListSchema } from 'entities/WebMcp/model/schemas'
import { useContactsPageContext } from 'pages/contacts/context/contactsPageContext'

// isLoading covers the 500ms search debounce plus the fetch; wait it out before reading results.
const READY_TIMEOUT = 8000

const ContactsPageToolsInner = () => {
  const store = useContactsPageContext()

  useMcpTool({
    name: 'search_contacts_list',
    description:
      'Search the contacts list on the open Contacts page. Applies the term to the on-screen list (both the search box and the visible list update) and returns the matching contacts. Prefer this over search_contacts while the Contacts page is open, so the interface stays in sync with what you do. Pass an empty term to clear the search.',
    input: searchContactsListSchema,
    annotations: { readOnlyHint: true },
    handler: async ({ term }) => {
      try {
        const search = term ?? ''
        // Same entry point the search box uses: updates the input and drives the list load.
        store.contactsFilterStore.changeSearchAndResetPaginationPage(search)
        await when(() => !store.contactsTableStore.isLoading, { timeout: READY_TIMEOUT })
        const results = store.contactsTableStore.getContactsList.slice(0, 25).map((c) => ({
          id: c.id,
          name: c.name,
          number: c.phoneInfo,
          email: c.email || null,
        }))
        return okResult({
          applied_search: search,
          total: store.contactsTableStore.total,
          showing: results.length,
          results,
          note: 'The on-screen contacts list is now filtered to this search.',
        })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  return null
}

/**
 * Page-scoped WebMCP tool that drives the Contacts list UI (the contextual
 * UI-driving pattern). Mounted with the Contacts page, bound to the live
 * ContactsPageStore, so searching through the agent updates the on-screen list
 * rather than answering only in the chat. Gated by the agent flag; the inner
 * WebMCPProvider matches the global one so useMcpTool registers on the same
 * navigator.modelContext.
 */
export const ContactsPageTools = observer(() => {
  const enabled = useWebMcpEnabled()
  if (!enabled) return null
  return (
    <WebMCPProvider name='salesmsg-contacts-page' version='0.1.0'>
      <ContactsPageToolsInner />
    </WebMCPProvider>
  )
})
